import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { getUserDisplayName, getUserInitial, getUserAvatarUrl } from '@/lib/utils';
import { AdminUserLink } from '@/components/AdminUserLink';
import { VoterAvatars } from '@/components/VoterAvatars';
import { SimilarPostItem } from '@/components/SimilarPostItem';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Link2, Check, Calendar } from 'lucide-react';
import { format } from 'date-fns';

interface PostDetailsSidebarProps {
  postId: string;
  postTitle: string;
  boardId: string;
  status: {
    label: string;
    variant: 'open' | 'review' | 'planned' | 'progress' | 'complete' | 'closed';
  };
  category?: {
    id: string;
    name: string;
    color: string;
  } | null;
  estimatedCompletionDate?: string | null;
}

export function PostDetailsSidebar({
  postId,
  postTitle,
  boardId,
  status,
  category,
  estimatedCompletionDate,
}: PostDetailsSidebarProps) {
  const [votersModalOpen, setVotersModalOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Initial voter query for sidebar display
  const { data, isLoading } = useQuery({
    queryKey: ['voters', postId],
    queryFn: () => api.votes.voters(postId, { limit: 10 }),
  });

  // Query all voters when modal is open
  const { data: allVotersData, isLoading: allVotersLoading } = useQuery({
    queryKey: ['voters', postId, 'all'],
    queryFn: () => api.votes.voters(postId, { limit: 200 }),
    enabled: votersModalOpen,
  });

  // Query similar posts
  const { data: similarData } = useQuery({
    queryKey: ['similarPosts', boardId, postTitle],
    queryFn: () => api.posts.similar({ boardId, title: postTitle, limit: 5 }),
    staleTime: 30 * 1000,
    enabled: !!boardId && postTitle.length >= 3,
  });

  const voterCount = data?.pagination?.total || 0;
  const allVoters = allVotersData?.voters || [];
  const relatedPosts = (similarData?.posts || []).filter(p => p.id !== postId).slice(0, 3);

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    toast.success('Link copied');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="sticky top-6 space-y-6">
      {/* Status */}
      <div>
        <h4 className="text-xs font-medium text-muted-foreground mb-2">Status</h4>
        <Badge variant={status.variant} className="rounded-full px-2.5 py-0.5 text-xs gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
          {status.label}
        </Badge>
      </div>

      {/* Estimated Completion Date - shown for planned posts */}
      {status.variant === 'planned' && estimatedCompletionDate && (
        <div>
          <h4 className="text-xs font-medium text-muted-foreground mb-2">Estimated Completion</h4>
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-purple-500" />
            <span>{format(new Date(estimatedCompletionDate), 'MMMM d, yyyy')}</span>
          </div>
        </div>
      )}

      {/* Category */}
      <div>
        <h4 className="text-xs font-medium text-muted-foreground mb-2">Category</h4>
        {category ? (
          <Badge
            variant="outline"
            className="text-xs"
            style={{
              backgroundColor: `${category.color}15`,
              color: category.color,
              borderColor: category.color,
            }}
          >
            {category.name}
          </Badge>
        ) : (
          <span className="text-sm text-muted-foreground">None</span>
        )}
      </div>

      {/* Voters */}
      <div>
        <h4 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          Voters
        </h4>

        {isLoading ? (
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center gap-2">
                <Skeleton className="h-6 w-6 rounded-full" />
                <Skeleton className="h-3 w-20" />
              </div>
            ))}
          </div>
        ) : voterCount === 0 ? (
          <span className="text-sm text-muted-foreground">Be the first to vote!</span>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <VoterAvatars postId={postId} maxDisplay={8} size="sm" />
              {voterCount > 0 && (
                <span className="text-lg font-semibold">{voterCount}</span>
              )}
            </div>
            {voterCount > 8 && (
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0 text-xs"
                onClick={() => setVotersModalOpen(true)}
              >
                View all voters
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Share Button */}
      <div>
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={handleCopyLink}
        >
          {copied ? (
            <>
              <Check className="h-4 w-4 mr-2" />
              Copied!
            </>
          ) : (
            <>
              <Link2 className="h-4 w-4 mr-2" />
              Share
            </>
          )}
        </Button>
      </div>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <div>
          <h4 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            Related Requests
          </h4>
          <div className="space-y-2">
            {relatedPosts.map((post) => (
              <SimilarPostItem key={post.id} post={post} />
            ))}
          </div>
        </div>
      )}

      {/* All Voters Modal */}
      <Dialog open={votersModalOpen} onOpenChange={setVotersModalOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>All Voters ({voterCount})</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-80">
            {allVotersLoading ? (
              <div className="space-y-2 py-2">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Skeleton className="h-8 w-8 rounded-full" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-2 py-2">
                {allVoters.map((voter) => (
                  <div key={voter.id} className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarImage
                        src={getUserAvatarUrl(voter) || undefined}
                        alt={getUserDisplayName(voter)}
                      />
                      <AvatarFallback className="text-xs" colorSeed={voter.id}>
                        {getUserInitial(voter)}
                      </AvatarFallback>
                    </Avatar>
                    <AdminUserLink userId={voter.id} className="text-sm text-foreground">
                      {getUserDisplayName(voter)}
                    </AdminUserLink>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function PostDetailsSidebarSkeleton() {
  return (
    <div className="sticky top-6 space-y-6">
      <div>
        <Skeleton className="h-3 w-14 mb-2" />
        <Skeleton className="h-5 w-20" />
      </div>
      <div>
        <Skeleton className="h-3 w-16 mb-3" />
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center gap-2">
              <Skeleton className="h-6 w-6 rounded-full" />
              <Skeleton className="h-3 w-20" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
